// ═══════════════════════════════════════════════════════════════════════════════
// Membership level comparison modal — same pricing table as marketing site
// ═══════════════════════════════════════════════════════════════════════════════

import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import {
  isMembershipPricingLocale,
  type Language,
  type MembershipPricingLocale,
  type PublicMembershipPaidIntervalPricing,
  type PublicMembershipPricingCell,
  type PublicMembershipPricingPromo,
  type PublicMembershipPricingResponse,
  type PublicMembershipPricingRow,
} from '@fdh/shared';
import { useLanguage } from '../../contexts/LanguageContext';
import { publicMembershipPricingApi } from '../../lib/api';
import { AppModal } from '../ui/AppModal';

type MembershipPricingCompareModalProps = {
  open: boolean;
  onClose: () => void;
};

const toPricingLocale = (language: Language): MembershipPricingLocale => {
  if (isMembershipPricingLocale(language)) {
    return language;
  }
  // id / fil fall back to English (same as marketing FAQ / pricing)
  return 'en';
};

const formatMoney = (currency: string, amount: number): string => {
  const code = String(currency || 'HKD').toUpperCase();
  return `${code}$${amount}`;
};

type PricingStrings = {
  priceUnavailable: string;
  monthlyFeeLabel: string;
  yearlyFeeLabel: string;
  cheaperBadge: string;
  discountUntil: string;
  welcomeRemark: string;
  trialStartsOnRegister: string;
  yes: string;
  no: string;
};

type IntervalDisplay = {
  currentMajor: number;
  listMajor: number | null;
  remark: string | null;
};

const applyPlainTemplate = (template: string, values: Record<string, string>): string => {
  return Object.entries(values).reduce(
    (text, [key, value]) => text.split(`{${key}}`).join(value),
    template,
  );
};

const resolveIntervalDisplay = (
  currency: string,
  intervalPricing: PublicMembershipPaidIntervalPricing,
  strings: PricingStrings,
): IntervalDisplay | null => {
  if (intervalPricing.listPriceMajor == null) {
    return null;
  }

  const list = intervalPricing.listPriceMajor;
  const promo = intervalPricing.promo;

  if (!promo) {
    return { currentMajor: list, listMajor: null, remark: null };
  }

  if (promo.type === 'limited_time') {
    return {
      currentMajor: promo.promoPriceMajor,
      listMajor: promo.listPriceMajor,
      remark: applyPlainTemplate(strings.discountUntil, { date: promo.endsAtDate }),
    };
  }

  if (promo.type === 'welcome') {
    return {
      currentMajor: promo.firstMonthPriceMajor,
      listMajor: promo.thereafterPriceMajor,
      remark: applyPlainTemplate(strings.welcomeRemark, {
        first: formatMoney(currency, promo.firstMonthPriceMajor),
        thereafter: formatMoney(currency, promo.thereafterPriceMajor),
      }),
    };
  }

  if (promo.type === 'welcome_and_limited') {
    return {
      currentMajor: promo.firstMonthPriceMajor,
      listMajor: promo.listPriceMajor,
      remark: applyPlainTemplate(strings.discountUntil, { date: promo.endsAtDate }),
    };
  }

  return { currentMajor: list, listMajor: null, remark: null };
};

const PaidPriceLine = ({
  label,
  currency,
  display,
  showRemark = false,
}: {
  label: string;
  currency: string;
  display: IntervalDisplay;
  showRemark?: boolean;
}): ReactElement => (
  <span className="inline-flex flex-wrap items-center gap-1 text-sm leading-snug">
    <span className="font-medium text-gray-700 dark:text-gray-200">{label}</span>
    <span className="font-semibold text-primary-600 dark:text-primary-400">
      {formatMoney(currency, display.currentMajor)}
    </span>
    {display.listMajor != null && display.listMajor !== display.currentMajor ? (
      <span className="text-gray-400 line-through">{formatMoney(currency, display.listMajor)}</span>
    ) : null}
    {showRemark && display.remark ? <RowRemarkTooltip remark={display.remark} /> : null}
  </span>
);

const renderPaidPricing = (
  cell: Extract<PublicMembershipPricingCell, { kind: 'pricing'; mode: 'paid' }>,
  strings: PricingStrings,
): ReactNode => {
  const currency = cell.currency || 'HKD';
  const monthly = cell.monthly ?? {
    listPriceMajor: cell.listPriceMajor,
    promo: cell.promo as PublicMembershipPricingPromo | null,
  };
  const yearly = cell.yearly ?? { listPriceMajor: null, promo: null };

  const monthlyDisplay =
    monthly.listPriceMajor == null ? null : resolveIntervalDisplay(currency, monthly, strings);
  const yearlyDisplay =
    yearly.listPriceMajor == null ? null : resolveIntervalDisplay(currency, yearly, strings);

  if (!monthlyDisplay && !yearlyDisplay) {
    return <span className="text-gray-500 dark:text-gray-400">{strings.priceUnavailable}</span>;
  }

  return (
    <span className="flex flex-col items-start gap-1">
      {monthlyDisplay ? (
        <PaidPriceLine
          label={strings.monthlyFeeLabel}
          currency={currency}
          display={monthlyDisplay}
          showRemark
        />
      ) : null}
      {yearlyDisplay ? (
        <PaidPriceLine
          label={strings.yearlyFeeLabel}
          currency={currency}
          display={yearlyDisplay}
          showRemark
        />
      ) : null}
      {yearlyDisplay ? (
        <span className="inline-flex rounded-md bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">
          {strings.cheaperBadge}
        </span>
      ) : null}
    </span>
  );
};

const PricingCell = ({
  cell,
  strings,
}: {
  cell: PublicMembershipPricingCell | null | undefined;
  strings: PricingStrings;
}): ReactElement => {
  if (!cell) {
    return <>—</>;
  }
  if (cell.kind === 'boolean') {
    if (cell.value) {
      return (
        <span className="inline-flex text-emerald-600 dark:text-emerald-400" aria-label={strings.yes}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5" aria-hidden>
            <path
              fillRule="evenodd"
              d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      );
    }
    return (
      <span className="inline-flex text-gray-400 dark:text-gray-500" aria-label={strings.no}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5" aria-hidden>
          <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
        </svg>
      </span>
    );
  }
  if (cell.kind === 'pricing') {
    if (cell.mode === 'paid') {
      return <>{renderPaidPricing(cell, strings)}</>;
    }
    if (cell.mode === 'trial') {
      return (
        <span className="inline-flex items-center gap-1">
          <span>{cell.text || ''}</span>
          <RowRemarkTooltip remark={strings.trialStartsOnRegister} />
        </span>
      );
    }
    return <>{cell.text || ''}</>;
  }
  return <>{cell.text || ''}</>;
};

const RowRemarkTooltip = ({ remark }: { remark: string }): ReactElement => {
  const tooltipId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; place: 'above' | 'below' }>({
    top: 0,
    left: 0,
    place: 'above',
  });

  const updatePosition = (): void => {
    const el = triggerRef.current;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    const tooltipWidth = 224;
    const gap = 8;
    const preferredLeft = rect.left + rect.width / 2 - tooltipWidth / 2;
    const maxLeft = window.innerWidth - tooltipWidth - 8;
    const left = Math.max(8, Math.min(preferredLeft, maxLeft));
    const placeAbove = rect.top > 64;
    if (placeAbove) {
      setPosition({ top: rect.top - gap, left, place: 'above' });
      return;
    }
    setPosition({ top: rect.bottom + gap, left, place: 'below' });
  };

  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    updatePosition();
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handleReposition = (): void => {
      updatePosition();
    };
    const handlePointerDown = (event: MouseEvent | TouchEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (triggerRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [open]);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-gray-300 text-[10px] font-bold text-gray-500 hover:border-primary-500 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-400"
        aria-label={remark}
        aria-expanded={open}
        aria-describedby={open ? tooltipId : undefined}
        tabIndex={0}
        onClick={() => {
          setOpen((prev) => !prev);
        }}
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        onFocus={handleOpen}
        onBlur={handleClose}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            handleClose();
          }
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen((prev) => !prev);
          }
        }}
      >
        ?
      </button>
      {open && typeof document !== 'undefined'
        ? createPortal(
            <span
              id={tooltipId}
              role="tooltip"
              className={`pointer-events-none fixed z-[80] w-56 rounded-lg bg-gray-900 px-3 py-2 text-left text-xs font-normal text-white shadow-lg dark:bg-gray-700 ${
                position.place === 'above' ? '-translate-y-full' : ''
              }`}
              style={{ top: position.top, left: position.left }}
            >
              {remark}
            </span>,
            document.body,
          )
        : null}
    </>
  );
};

const RowLabel = ({ row }: { row: PublicMembershipPricingRow }): ReactElement => {
  if (!row.remark) {
    return <>{row.label}</>;
  }
  return (
    <span className="inline-flex items-center gap-1">
      {row.label}
      <RowRemarkTooltip remark={row.remark} />
    </span>
  );
};

export const MembershipPricingCompareModal = ({
  open,
  onClose,
}: MembershipPricingCompareModalProps): ReactElement | null => {
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PublicMembershipPricingResponse | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    const load = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const locale = toPricingLocale(language);
        const res = await publicMembershipPricingApi.get(locale);
        if (cancelled) {
          return;
        }
        if (res.success && res.data) {
          setData(res.data);
        } else {
          setError(res.error ?? t('membership.pricing.loadError'));
        }
      } catch {
        if (!cancelled) {
          setError(t('membership.pricing.loadError'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [open, language, t]);

  if (!open) {
    return null;
  }

  const strings: PricingStrings = {
    priceUnavailable: t('membership.pricing.priceUnavailable'),
    monthlyFeeLabel: t('membership.pricing.monthlyFeeLabel'),
    yearlyFeeLabel: t('membership.pricing.yearlyFeeLabel'),
    cheaperBadge: t('membership.pricing.cheaperBadge'),
    discountUntil: t('membership.pricing.discountUntil'),
    welcomeRemark: t('membership.pricing.welcomeRemark'),
    trialStartsOnRegister: t('membership.pricing.trialStartsOnRegister'),
    yes: t('membership.pricing.yes'),
    no: t('membership.pricing.no'),
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={t('membership.pricing.compareTitle')}
      titleId="membership-pricing-compare-title"
      closeAriaLabel={t('common.close')}
      panelMaxWidthClass="max-w-4xl"
      panelScrollClassName="max-h-[calc(100dvh-2rem)] overflow-hidden"
      panelClassName="flex flex-col"
    >
      <div className="min-h-0 flex-1">
        {loading ? (
          <p className="px-2 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            {t('membership.pricing.loading')}
          </p>
        ) : null}

        {error ? (
          <p className="px-2 py-8 text-center text-sm text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        ) : null}

        {!loading && !error && data ? (
          <div className="max-h-[min(70dvh,calc(100dvh-10rem))] overflow-auto rounded-xl border border-gray-200 dark:border-gray-700">
            <table className="min-w-full border-separate border-spacing-0 text-left text-sm">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="sticky left-0 top-0 z-30 border-b border-gray-200 bg-gray-50 px-3 py-3 font-semibold text-gray-900 dark:border-gray-700 dark:bg-gray-800 sm:px-4"
                  />
                  {data.plans.map((plan) => (
                    <th
                      key={plan.level}
                      scope="col"
                      className="sticky top-0 z-20 border-b border-gray-200 bg-gray-50 px-3 py-3 font-semibold text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:px-4"
                    >
                      {plan.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.key}>
                    <th
                      scope="row"
                      className="sticky left-0 z-10 border-b border-gray-100 bg-white px-3 py-3 font-medium text-gray-700 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-200 sm:px-4"
                    >
                      <RowLabel row={row} />
                    </th>
                    {data.plans.map((plan) => {
                      const levelKey = String(plan.level) as '1' | '2' | '3';
                      const cell = row.values[levelKey];
                      return (
                        <td
                          key={`${row.key}-${plan.level}`}
                          className="border-b border-gray-100 bg-white px-3 py-3 text-gray-600 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300 sm:px-4"
                        >
                          <PricingCell cell={cell} strings={strings} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </AppModal>
  );
};
